/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import Image from 'next/image';
import { z } from 'zod';

import { UserDetailInfo } from '@/app/(pawtree)/account/login/page';
import AlertCircle from '@/assets/images/alert-circle.svg';
import Button from '@/components/V2/Button';
import ButtonLink from '@/components/V2/ButtonLink';
import PasswordInput from '@/components/V2/PasswordInput/PasswordInput';
import PasswordStrengthBar from '@/components/V2/PasswordStrengthBar/PasswordStrengthBar';
import AuthToast from '@/components/V2/Popup/AuthToast';
import TextInput from '@/components/V2/TextInput';
import { useSignup } from '@/contexts/SignupContext';
import { PHONE_NUMBER_REGEX } from '@/helpers/constants/regex';
import { useDebouncedValue } from '@/helpers/hooks/useDebounced';
import useForm from '@/helpers/hooks/useForm';
import { isPhoneNumberInputPotentiallyValid, sanitizePhoneInput } from '@/helpers/utils/phone';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useIsEmailAvailableMutation } from '@/store/api/authentication';

import Checkbox from '../Checkbox';
import Spinner from '../PetProfile/Spinner';
// Third-party sign-in is now handled exclusively from the Sign In form.
import Recaptcha from './ReCAPTCHA';

interface SignupFormProps {
  onSubmit?: (formData: SignupFormData) => void;
  onToggleLogin: (name: string) => void;
  userDetail: UserDetailInfo;
  setUserDetail: (data: UserDetailInfo) => void;
  isLoadingCorporateAccounts?: boolean;
  isSubmitting?: boolean;
}

const signupSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    mobile: z
      .string()
      .min(1, 'Mobile Phone is required')
      .regex(PHONE_NUMBER_REGEX, 'Enter a valid phone number (+1XXXXXXXXXX, or 10 digit number).'),
    // Production regex (US numbers only - digits only):
    // .regex(/^\d+$/, 'Please enter numbers only')
    // .min(10, 'Please enter a 10-digit mobile number')
    // .max(10, 'Please enter a 10-digit mobile number'),
    zipCode: z
      .string()
      .min(1, 'Zip code is required')
      .regex(/^[0-9\-]+$/, 'Please enter a ZIP code using only numbers and dashes.'),
    email: z.string().min(1, 'Email Address is required').email('Please enter a valid email address.'),
    password: z.string().min(1, 'Password is required'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    emailNotifications: z.boolean().optional(),
    smsNotifications: z.boolean().optional(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Your passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof signupSchema>;
type SignupFormErrors = Partial<Record<keyof SignupFormData, string>>;

const ERROR_BORDER_CLASS = '!v2-border-[#FDA29B] focus:!v2-border-[#FDA29B] focus:!v2-shadow-none';
const ERROR_TEXT_CLASS = 'v2-text-[#D92D20]';
const EMAIL_TAKEN_ERROR = 'This email is already associated with another account.';
const TERMINATED_ERROR =
  'Your account was previously terminated. Please contact support at 855-940-5234 or support@pawtree.com.';

const SignupForm: React.FC<SignupFormProps> = ({
  onSubmit,
  onToggleLogin,
  userDetail,
  setUserDetail,
  isLoadingCorporateAccounts = false,
  isSubmitting = false,
}) => {
  const initialState = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      mobile: '',
      zipCode: '',
      email: '',
      password: '',
      confirmPassword: '',
      emailNotifications: false,
      smsNotifications: false,
    }),
    []
  );

  const {
    data,
    errors,
    setFormErrors,
    handleChange,
    // validation happens on blur only for email UX
    setFieldValue,
    onSubmit: handleFormSubmit,
  } = useForm<SignupFormData, SignupFormErrors>({
    initialState,
    errors: {} as SignupFormErrors,
    validationSchema: signupSchema as any,
  });

  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const EMAIL_FORMAT_ERROR = 'Please enter a valid email address.';

  const { setFormData } = useSignup();
  const [isEmailAvailableMutation] = useIsEmailAvailableMutation();

  // State for reCAPTCHA verification
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState<boolean>(false);

  // Debounce email value to avoid API calls on every keystroke
  const debouncedEmail = useDebouncedValue(data.email, 500);

  const [isMobileFocused, setIsMobileFocused] = useState(false);

  const handleCheckboxChange = (checkboxName: 'emailNotifications' | 'smsNotifications') => (value: boolean) => {
    setFieldValue(checkboxName, value);
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizePhoneInput(e.target.value);
    setFieldValue('mobile', value);
  };

  const isMobileInputPotentiallyValid = isPhoneNumberInputPotentiallyValid(data.mobile);
  const shouldShowMobileError = Boolean(errors.mobile) && (!isMobileFocused || !isMobileInputPotentiallyValid);

  // Helper function to handle email availability errors
  const handleEmailAvailabilityError = (response: any) => {
    const backendMessage = response?.data?.message || '';
    const isTerminated = backendMessage.toLowerCase().includes('terminated');
    const messageToShow = isTerminated ? TERMINATED_ERROR : EMAIL_TAKEN_ERROR;

    setFormErrors({
      ...errors,
      email: messageToShow,
    });

    toast(({ closeToast }) => <AuthToast type="error" message={messageToShow} closeToast={closeToast} />, {
      containerId: AUTH_MODAL_TOAST_CONTAINER,
    });
  };

  // Helper function to clear email availability errors
  const clearEmailAvailabilityError = () => {
    if (errors.email === EMAIL_TAKEN_ERROR || errors.email === TERMINATED_ERROR) {
      setFormErrors({
        ...errors,
        email: undefined,
      });
    }
  };

  // Check email availability when debounced email changes
  useEffect(() => {
    const checkEmailAvailability = async () => {
      // Only check if email is valid format
      if (debouncedEmail && debouncedEmail.includes('@') && debouncedEmail.includes('.')) {
        try {
          const response = await isEmailAvailableMutation({ email: debouncedEmail }).unwrap();
          if (!response.data.isEmailAvailable) {
            handleEmailAvailabilityError(response);
          } else {
            clearEmailAvailabilityError();
          }
        } catch (error) {
          console.error('Email availability check failed:', error);
        }
      }
    };

    checkEmailAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail]);

  const onFormSubmit = () => {
    // Check if reCAPTCHA is verified
    if (!isRecaptchaVerified) {
      toast(({ closeToast }) => <AuthToast type="error" message="Please enter captcha" closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
      return;
    }

    if (errors.email) {
      return;
    }

    handleFormSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>, () => {
      // Prepare form data object
      const formDataToSubmit: SignupFormData = {
        firstName: data.firstName,
        lastName: data.lastName,
        mobile: data.mobile,
        zipCode: data.zipCode,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        emailNotifications: data.emailNotifications,
        smsNotifications: data.smsNotifications,
      };

      // Save form data to signup context
      setFormData(formDataToSubmit);

      // Call parent onSubmit handler with form data
      onSubmit && onSubmit(formDataToSubmit);
    });
  };

  const onThirdPartyError = () => {
    toast(
      ({ closeToast }) => (
        <AuthToast
          type="error"
          message={'Sign-up requires profile access. Please enable permissions or select another way to continue.'}
          closeToast={closeToast}
        />
      ),
      {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      }
    );
  };

  return (
    <div className="">
      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-2xl ms:v2-text-3xl v2-text-[#101828] v2-mb-4 sm:v2-mb-8">
        Sign up
      </h2>
      <div className="v2-flex v2-flex-col v2-gap-4 v2-mb-4">
        <div className="v2-grid v2-grid-cols-1 md:v2-grid-cols-2 v2-gap-5">
          <div>
            <label
              htmlFor=""
              className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
            >
              First Name<span>*</span>
            </label>
            <TextInput
              type="text"
              name="firstName"
              placeholder="First Name"
              inputClassName={`v2-font-sweet-sans-pro placeholder:!v2-text-[#667085] ${
                errors.firstName ? ERROR_BORDER_CLASS : ''
              }`}
              value={data.firstName}
              onChange={handleChange}
              rightIconComponent={
                errors.firstName ? (
                  <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
                ) : undefined
              }
            />
            {errors.firstName && (
              <p className={`v2-font-sweet-sans-pro v2-text-sm v2-font-light ${ERROR_TEXT_CLASS}`}>
                {errors.firstName}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor=""
              className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
            >
              Last Name<span>*</span>
            </label>
            <TextInput
              type="text"
              name="lastName"
              placeholder="Last Name"
              inputClassName={`v2-font-sweet-sans-pro placeholder:!v2-text-[#667085] ${
                errors.lastName ? ERROR_BORDER_CLASS : ''
              }`}
              value={data.lastName}
              onChange={handleChange}
              rightIconComponent={
                errors.lastName ? (
                  <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
                ) : undefined
              }
            />
            {errors.lastName && (
              <p className={`v2-font-sweet-sans-pro v2-text-sm v2-font-light ${ERROR_TEXT_CLASS}`}>{errors.lastName}</p>
            )}
          </div>
          <div>
            <label
              htmlFor=""
              className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
            >
              Mobile<span>*</span>
            </label>
            <TextInput
              type="text"
              name="mobile"
              placeholder="Mobile"
              inputClassName={`v2-font-sweet-sans-pro placeholder:!v2-text-[#667085] ${
                shouldShowMobileError ? ERROR_BORDER_CLASS : ''
              }`}
              value={data.mobile}
              onChange={handleMobileChange}
              onFocus={() => setIsMobileFocused(true)}
              onBlur={() => setIsMobileFocused(false)}
              rightIconComponent={
                shouldShowMobileError ? (
                  <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
                ) : undefined
              }
            />
            {shouldShowMobileError && (
              <p className={`v2-font-sweet-sans-pro v2-text-sm v2-font-light ${ERROR_TEXT_CLASS}`}>{errors.mobile}</p>
            )}
          </div>
          <div>
            <label
              htmlFor=""
              className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
            >
              Zip Code<span>*</span>
            </label>
            <TextInput
              type="text"
              name="zipCode"
              placeholder="Zip Code"
              inputClassName={`v2-font-sweet-sans-pro placeholder:!v2-text-[#667085] ${
                errors.zipCode ? ERROR_BORDER_CLASS : ''
              }`}
              value={data.zipCode}
              onChange={handleChange}
              rightIconComponent={
                errors.zipCode ? (
                  <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
                ) : undefined
              }
            />
            {errors.zipCode && (
              <p className={`v2-font-sweet-sans-pro v2-text-sm v2-font-light ${ERROR_TEXT_CLASS}`}>{errors.zipCode}</p>
            )}
          </div>
        </div>
        <div className="v2-grid v2-grid-cols-1 v2-gap-5">
          <div>
            <label
              htmlFor=""
              className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
            >
              Email<span>*</span>
            </label>
            <TextInput
              type="email"
              name="email"
              placeholder="Email"
              inputClassName={`v2-font-sweet-sans-pro placeholder:!v2-text-[#667085] ${
                (isEmailFocused && errors.email === EMAIL_FORMAT_ERROR ? false : !!errors.email) && isEmailTouched
                  ? ERROR_BORDER_CLASS
                  : ''
              }`}
              value={data.email}
              onChange={handleChange}
              onBlur={() => {
                setIsEmailTouched(true);
                setIsEmailFocused(false);
              }}
              onFocus={() => setIsEmailFocused(true)}
              rightIconComponent={
                (isEmailFocused && errors.email === EMAIL_FORMAT_ERROR ? undefined : errors.email) && isEmailTouched ? (
                  <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
                ) : undefined
              }
            />
            {isEmailTouched && (!isEmailFocused || errors.email !== EMAIL_FORMAT_ERROR) && errors.email && (
              <p className={`v2-font-sweet-sans-pro v2-text-sm v2-font-light ${ERROR_TEXT_CLASS}`}>{errors.email}</p>
            )}
          </div>
        </div>
        <div className="v2-grid v2-grid-cols-1 md:v2-grid-cols-2 v2-gap-5">
          <PasswordInput
            name="password"
            label="Password*"
            placeholder="Password"
            value={data.password}
            onChange={handleChange}
            error={errors.password}
            inputClassName="placeholder:!v2-text-[#667085]"
          />
          <PasswordInput
            name="confirmPassword"
            label="Confirm Password*"
            inputClassName="placeholder:!v2-text-[#667085]"
            placeholder="Confirm Password"
            value={data.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
          />
        </div>
        <PasswordStrengthBar password={data.password} />
        <Recaptcha
          onChange={token => {
            setRecaptchaToken(token);
            setIsRecaptchaVerified(!!token);
          }}
        />
        <div className="v2-flex v2-items-start v2-gap-2">
          <Checkbox
            id="email-notifications"
            name="emailNotifications"
            value={data.emailNotifications}
            onChange={handleCheckboxChange('emailNotifications')}
            containerClassName="v2-flex-shrink-0"
            childrenClassName="v2-hidden"
            inputClassName=""
          >
            <span></span>
          </Checkbox>
          <label
            htmlFor="email-notifications"
            className="v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro v2-leading-5 v2-cursor-pointer v2-flex-1"
          >
            I Would Like To Receive Email Receipts And Order Notifications
          </label>
        </div>
        <div className="v2-flex v2-items-start v2-gap-2">
          <Checkbox
            id="sms-notifications"
            name="smsNotifications"
            value={data.smsNotifications}
            onChange={handleCheckboxChange('smsNotifications')}
            containerClassName="v2-flex-shrink-0"
            childrenClassName="v2-hidden"
            inputClassName=""
          >
            <span></span>
          </Checkbox>
          <label
            htmlFor="sms-notifications"
            className="v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro v2-leading-5 v2-cursor-pointer v2-flex-1"
          >
            Opt-in for convenience! By checking this box, you agree to receive SMS notifications about your orders,
            special discounts, and early access or notification on our sales events. Standard messaging rates apply. You
            can opt-out at any time.
          </label>
        </div>
      </div>
      <Button
        btnSize="md"
        scheme="primary"
        variant="filled"
        type="button"
        className="v2-font-sweet-sans-pro-bold v2-font-bold v2-w-full v2-mb-3 v2-flex v2-items-center v2-justify-center v2-gap-2"
        onClick={onFormSubmit}
        disabled={isLoadingCorporateAccounts || isSubmitting}
        id="register-button"
      >
        {isLoadingCorporateAccounts ? 'Loading...' : isSubmitting ? 'Loading...' : 'Get started'}
        {isSubmitting && <Spinner height={20} />}
      </Button>
      {/* Third-party buttons removed: handled from Sign In form now */}
      <div className="v2-flex v2-items-center v2-justify-center">
        <p className="v2-text-[#475467] v2-text-sm v2-font-sweet-sans-pro v2-font-normal v2-flex v2-items-center v2-gap-1 v2-whitespace-nowrap">
          Already have an account?
          <ButtonLink
            scheme={'primary'}
            variant={'outlined'}
            className="!v2-border-0 !v2-p-0 !v2-rounded-0 v2-font-sweet-sans-pro-bold v2-font-bold !v2-text-sm v2-w-fit v2-whitespace-nowrap"
            onClick={() => onToggleLogin('login')}
          >
            Sign in
          </ButtonLink>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
